import {
  type FavoriteVehicle,
  type FinanceInfo,
  FulfillmentKeyMap,
  InventoryTypeId,
  type InventoryVehicle,
  RequestedFeature,
  type TradeInfo
} from '@carvana/merch-contracts';
import {
  type LoadableState,
  useEffectWithController,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { type AxiosInstance } from 'axios';
import { useRef } from 'react';
import { inventory } from './constants/apis';
import defaultVehicle from './constants/defaultVehicle';
import type { InventoryResponse } from './contracts';

const commonRequestedFeatures = [
  RequestedFeature.FetchTradeIn,
  RequestedFeature.LoanTermPricing
];

function isDefined<T>(input: T | undefined | null): input is T {
  return typeof input !== 'undefined' && input !== null;
}

/**
 * Favorites API returns vehicles with the most recently favorited first, so we
 * need to sort the vehicles returned from search to match that order.
 */
export function sortVehiclesByRecency(
  vehicleFromSearch: InventoryVehicle[],
  vehicleIdsFromFavorites: number[]
) {
  const vehicleMap = vehicleFromSearch.reduce<Record<number, InventoryVehicle>>(
    (acc, vehicle) => {
      acc[vehicle.vehicleId] = vehicle;

      return acc;
    },
    {}
  );

  return vehicleIdsFromFavorites.map(id => vehicleMap[id]).filter(isDefined);
}

type OwnArgs = {
  analyticsData: {
    browser: string;
    clientId: string;
    deviceName: string;
    isMobileDevice: boolean;
    referrer: string;
  };

  browserCookieId: string;

  /**
   * Deployment environment
   */
  environment: 'dev' | 'test' | 'prod';

  /**
   * List of favorite vehicles to hydrate from search results
   */
  favoriteVehicles: FavoriteVehicle[];

  /**
   * Axios instance with authentication headers set
   */
  fetcherWithAuth: AxiosInstance;

  /**
   * Whether the user has applied a trade-in vehicle
   */
  hasAppliedTrade?: boolean;

  /**
   * If provided, this loadable state will be used instead of internal state
   */
  inventoryVehicles?: LoadableState<InventoryVehicle[]>;

  /**
   * Callback for handling errors
   */
  onError?: (err: unknown) => void;

  /**
   * Callback fired when finance info is fetched
   */
  onFetchedFinanceInfo?: (financeInfo: FinanceInfo) => void;

  /**
   * Callback fired when trade info is fetched
   */
  onFetchedTradeInfo?: (tradeInfo?: TradeInfo) => void;

  /**
   * The preferred acquisition identifier
   */
  preferredTrade?: string;

  /**
   * If false, the fetch will be skipped (defaults to true)
   */
  shouldFetch?: boolean;

  zip: string;
};

export default function useHydrateFavoritesFromSearch(
  args: OwnArgs
): LoadableState<InventoryVehicle[]> {
  const {
    fetcherWithAuth: { post },
    onError,
    onFetchedFinanceInfo,
    onFetchedTradeInfo
  } = args;

  const onErrorRef = useRef(onError);
  const onFetchedFinanceInfoRef = useRef(onFetchedFinanceInfo);
  const onFetchedTradeInfoRef = useRef(onFetchedTradeInfo);

  // Keep refs up to date
  onErrorRef.current = onError;
  onFetchedFinanceInfoRef.current = onFetchedFinanceInfo;
  onFetchedTradeInfoRef.current = onFetchedTradeInfo;

  const inventoryVehiclesFallback = useLoadableState<InventoryVehicle[]>([]);
  const inventoryVehicles = args.inventoryVehicles ?? inventoryVehiclesFallback;
  const { setData, setError, setLoading } = inventoryVehicles;

  const analyticsDataRef = useRef(args.analyticsData);
  analyticsDataRef.current = args.analyticsData;

  useEffectWithController(
    controller => {
      if (args.shouldFetch === false) {
        return;
      }

      if (args.favoriteVehicles.length === 0) {
        setData([]);

        return;
      }

      const vehicleIds = args.favoriteVehicles.map(v => v.vehicleId);

      setLoading();

      post<InventoryResponse>(
        inventory(args.environment),
        {
          analyticsData: analyticsDataRef.current,
          browserCookieId: args.browserCookieId,
          filters: { vehicleIds },
          pagination: { page: 1, pageSize: 50 },
          preferredAcquisitionName: args.hasAppliedTrade
            ? args.preferredTrade
            : undefined,
          requestedFeatures: args.hasAppliedTrade
            ? [...commonRequestedFeatures, RequestedFeature.ApplyTradeIn]
            : commonRequestedFeatures,
          sortBy: 'MostPopular',
          zip5: args.zip
        },
        { signal: controller.signal }
      )
        .then(res => {
          const {
            financeInfo,
            inventory: { vehicles },
            tradeInfo
          } = res.data;

          onFetchedFinanceInfoRef.current?.(financeInfo);
          onFetchedTradeInfoRef.current?.(tradeInfo);

          const vehiclesIncludingSold = args.favoriteVehicles.reduce<
            InventoryVehicle[]
          >((acc, vehicle, index: number) => {
            if (vehicle.saleStatus === 'Sold') {
              acc.push({
                ...defaultVehicle,
                ...vehicle,
                price: vehicle.price
                  ? { ...defaultVehicle.price, ...vehicle.price }
                  : undefined,
                previousPrice: 0,
                vehicleInventoryType: InventoryTypeId.Core,
                fulfillmentType: FulfillmentKeyMap[vehicle.fulfillmentType],
                vehicleTags: []
              });
            }

            if (index < vehicles.length) {
              acc.push(vehicles[index]);
            }

            return acc;
          }, []);

          setData(sortVehiclesByRecency(vehiclesIncludingSold, vehicleIds));
        })
        .catch(err => {
          setError(err);
          onErrorRef.current?.(err);
        });
    },
    [
      args.browserCookieId,
      args.environment,
      args.favoriteVehicles,
      args.hasAppliedTrade,
      args.preferredTrade,
      args.shouldFetch,
      args.zip,
      post,
      setData,
      setError,
      setLoading
    ]
  );

  return inventoryVehicles;
}
